import type { PopDevToolsEvent, PopDevToolsFilter, PopDevToolsLevel } from './types';

export interface PopDevToolsRingAppendResult {
  event: PopDevToolsEvent;
  dropped?: PopDevToolsEvent;
}

export type PopDevToolsEventDraft = Omit<PopDevToolsEvent, 'id'> & { id?: number };

const levelRank: Record<PopDevToolsLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const createSet = <T>(values?: readonly T[]) => (values?.length ? new Set(values) : undefined);

export class PopDevToolsRingBuffer {
  private readonly events: Array<PopDevToolsEvent | undefined>;
  private readonly lowercaseMessages: Array<string | undefined>;
  private start = 0;
  private length = 0;
  private nextId = 1;

  constructor(readonly capacity = 50_000) {
    if (!Number.isInteger(capacity) || capacity <= 0) throw new Error('PopDevToolsRingBuffer capacity must be positive');
    this.events = new Array(capacity);
    this.lowercaseMessages = new Array(capacity);
  }

  get size() {
    return this.length;
  }

  append(draft: PopDevToolsEventDraft): PopDevToolsRingAppendResult {
    const writeIndex = this.getWriteIndex();
    const dropped = this.length === this.capacity ? this.events[writeIndex] : undefined;
    const event = { ...draft, id: draft.id ?? this.nextId++ };
    this.events[writeIndex] = event;
    this.lowercaseMessages[writeIndex] = undefined;

    if (this.length === this.capacity) {
      this.start = (this.start + 1) % this.capacity;
    } else {
      this.length += 1;
    }

    if (event.id >= this.nextId) this.nextId = event.id + 1;
    return { event, dropped };
  }

  clear() {
    this.events.fill(undefined);
    this.lowercaseMessages.fill(undefined);
    this.start = 0;
    this.length = 0;
  }

  snapshot(filter?: PopDevToolsFilter): readonly PopDevToolsEvent[] {
    if (!filter) {
      const events: PopDevToolsEvent[] = [];
      this.forEach((event) => events.push(event));
      return events;
    }

    return this.filter(filter);
  }

  forEach(callback: (event: PopDevToolsEvent, orderedIndex: number) => void) {
    for (let orderedIndex = 0; orderedIndex < this.length; orderedIndex += 1) {
      const slot = this.getSlotForOrderedIndex(orderedIndex);
      const event = this.events[slot];
      if (event) callback(event, orderedIndex);
    }
  }

  filter(filter: PopDevToolsFilter): PopDevToolsEvent[] {
    const channels = createSet(filter.channel ? [filter.channel] : filter.channels);
    const excludedChannels = createSet(filter.excludeChannels);
    const levels = createSet(filter.level ? [filter.level] : filter.levels);
    const query = (filter.query ?? filter.text)?.trim().toLowerCase();
    const result: PopDevToolsEvent[] = [];

    for (let orderedIndex = 0; orderedIndex < this.length; orderedIndex += 1) {
      const slot = this.getSlotForOrderedIndex(orderedIndex);
      const event = this.events[slot];
      if (!event) continue;

      if (channels && !channels.has(event.channel)) continue;
      if (excludedChannels?.has(event.channel)) continue;
      if (levels && !levels.has(event.level)) continue;
      if (filter.minLevel && levelRank[event.level] < levelRank[filter.minLevel]) continue;
      if (filter.source && filter.source !== 'all' && event.source !== filter.source) continue;
      if (filter.sinceTs !== undefined && event.ts < filter.sinceTs) continue;
      if (filter.untilTs !== undefined && event.ts > filter.untilTs) continue;
      if (query && !this.getLowercaseMessageForSlot(slot).includes(query)) continue;
      if (filter.predicate && !filter.predicate(event)) continue;

      result.push(event);
    }

    return result;
  }

  getLowercaseMessage(event: PopDevToolsEvent) {
    for (let orderedIndex = 0; orderedIndex < this.length; orderedIndex += 1) {
      const slot = this.getSlotForOrderedIndex(orderedIndex);
      if (this.events[slot]?.id === event.id) return this.getLowercaseMessageForSlot(slot);
    }

    return event.message.toLowerCase();
  }

  private getWriteIndex() {
    return (this.start + this.length) % this.capacity;
  }

  private getSlotForOrderedIndex(orderedIndex: number) {
    return (this.start + orderedIndex) % this.capacity;
  }

  private getLowercaseMessageForSlot(slot: number) {
    const cached = this.lowercaseMessages[slot];
    if (cached !== undefined) return cached;

    const lower = this.events[slot]?.message.toLowerCase() ?? '';
    this.lowercaseMessages[slot] = lower;
    return lower;
  }
}
